import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subscription, forkJoin } from 'rxjs';
import { filter, finalize } from 'rxjs/operators';
import { AppState } from '../../../../store/app.state';
import {
  selectCurrentUser,
  selectAuthLoading,
} from '../../../../store/auth/auth.selectors';
import { loadCurrentUser } from '../../../../store/auth/auth.actions';
import { User } from '../../../../core/interfaces/user.interface';
import { ModalService } from '../../../../core/services/modal.service';
import { MedicalRecordService } from '../../../../core/services/medical-record.service';
import { SwalService } from '../../../../core/services/swal.service';
import {
  HealthCondition,
  MedicalRecordItem,
  GynecologicalHistory,
  FamilyHistory,
  FamilyMember,
} from '../../../../core/interfaces/health-condition.interface';
import { ComorbidityHistoryModalComponent } from '../../../../shared/components/modal/comorbidity-history-modal';
import { HealthConditionCommentModalComponent } from '../../../../shared/components/modal/health-condition-comment-modal';
import { FamilyMemberModalComponent } from '../../../../shared/components/modal/family-member-modal';
import { MedicalRecordHistoryModalComponent } from '../../../../shared/components/modal/medical-record-history-modal';
import { LucideAngularModule, History } from 'lucide-angular';

@Component({
  selector: 'app-medical-record',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule],
  templateUrl: './medical-record.component.html',
  styleUrls: ['./medical-record.component.scss'],
})
export class MedicalRecordComponent implements OnInit, OnDestroy {
  currentUser: User | null = null;
  loading = false;
  private subscription = new Subscription();

  readonly History = History;

  medicalRecordLoading = false;

  /** Antécédents personnels (API) - conservé pour compatibilité, préférer precedents */
  antecedentsPersonnels: (MedicalRecordItem & {
    code?: string;
    label?: string;
    isCurrent?: boolean;
  })[] = [];

  /** Habitudes de vie par défaut (si l'API ne retourne pas de habits) */
  habitsDefault: { code?: string; label: string; valueString?: string }[] = [
    { code: 'fumeur', label: 'Tabac', valueString: 'unknown' },
    { code: 'pratique-sport', label: 'Pratique sport', valueString: '' },
    { code: 'alcool', label: 'Ethylisme', valueString: 'unknown' },
    { code: 'habitudes-sexuelles', label: 'Habitudes Sexuelles', valueString: 'unknown' },
  ];

  /** Habitudes de vie affichées (API ou défaut) */
  habits: {
    code?: string;
    label: string;
    valueString?: string;
    note?: string;
    sevrage?: boolean;
    sevrageNote?: string;
  }[] = [];

  /** Comorbidités (API) - _id peut être null pour les items non encore créés */
  comorbidities: (Omit<HealthCondition, '_id'> & {
    _id: string | null;
    code?: string;
  })[] = [];

  /** Antécédents / Precedents (API) - même structure que comorbidities */
  precedents: (Omit<HealthCondition, '_id'> & {
    _id: string | null;
    code?: string;
  })[] = [];

  /** Créations en attente (validées dans le modal, pas encore envoyées à l'API) */
  pendingCreations: Array<{
    type: 'comorbidity' | 'precedent';
    code: string;
    label: string;
    text: string;
    clinicalStatus: 'active' | 'inactive' | 'unknown';
  }> = [];

  /** Désactivations en attente via l'ancien mécanisme switch (obsolète pour les radios) */
  pendingDeactivations: Array<{
    conditionId: string;
    type: 'comorbidity' | 'precedent';
    code: string;
    label: string;
  }> = [];

  /** Modifications d'habitudes en attente */
  pendingHabits: Array<{
    code: string;
    label: string;
    valueString: string;
    comment?: string;
    sevrage?: boolean;
    sevrageValue?: 'true' | 'false' | 'unknown';
    sevrageComment?: string;
  }> = [];

  gynecologicalHistory: GynecologicalHistory = {};
  pendingGyneco = false;

  familyHistory: FamilyHistory = {};
  familyMembers: FamilyMember[] = [];
  pendingFamily = false;

  savePendingLoading = false;

  constructor(
    private store: Store<AppState>,
    private modalService: ModalService,
    private medicalRecordService: MedicalRecordService,
    private swalService: SwalService,
  ) {}

  ngOnInit(): void {
    console.log(
      'MedicalRecordComponent: Initialisation - Récupération des données utilisateur...',
    );

    this.applyHabitsDefault();

    // Charger les données de l'utilisateur connecté
    this.loadUserData();

    // Surveiller les changements dans le store
    this.setupStoreSubscriptions();

    // Écouter la validation du modal de commentaire (création comorbidité/antécédent)
    this.subscription.add(
      this.modalService
        .getEvents()
        .pipe(filter((e) => e?.type === 'healthConditionCommentValidated'))
        .subscribe((event) =>
          this.onCreateHealthConditionFromModal(event.data),
        ),
    );

    this.subscription.add(
      this.modalService
        .getEvents()
        .pipe(filter((e) => e?.type === 'familyMemberAdded'))
        .subscribe((event) => this.onFamilyMemberAdded(event.data)),
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    console.log('MedicalRecordComponent: Souscriptions nettoyées');
  }

  private loadUserData(): void {
    console.log(
      'MedicalRecordComponent: Chargement des données utilisateur...',
    );

    // Dispatcher l'action pour charger l'utilisateur courant
    this.store.dispatch(loadCurrentUser());
  }

  private setupStoreSubscriptions(): void {
    // Surveiller l'utilisateur courant
    this.subscription.add(
      this.store.select(selectCurrentUser).subscribe((user) => {
        console.log(
          'MedicalRecordComponent: Utilisateur récupéré du store:',
          user,
        );
        this.currentUser = user;

        // Si l'utilisateur n'est pas chargé, essayer de le recharger
        if (!user) {
          console.log(
            'MedicalRecordComponent: Aucun utilisateur trouvé, rechargement...',
          );
          this.refreshUserData();
          this.clearMedicalData();
        } else if (user.passportNumber) {
          this.loadMedicalRecord(user.passportNumber);
        }
      }),
    );

    // Surveiller l'état de chargement
    this.subscription.add(
      this.store.select(selectAuthLoading).subscribe((isLoading) => {
        console.log('MedicalRecordComponent: État de chargement:', isLoading);
        this.loading = isLoading;
      }),
    );
  }

  refreshUserData(): void {
    console.log(
      'MedicalRecordComponent: Rafraîchissement des données utilisateur...',
    );

    // Dispatcher l'action pour recharger l'utilisateur
    this.store.dispatch(loadCurrentUser());
  }

  // Méthode pour forcer le rafraîchissement (peut être appelée depuis le template)
  forceRefresh(): void {
    console.log('MedicalRecordComponent: Forçage du rafraîchissement...');
    this.refreshUserData();
  }

  private applyHabitsDefault(): void {
    this.habits = [...this.habitsDefault];
  }

  private clearMedicalData(): void {
    this.comorbidities = [];
    this.precedents = [];
    this.antecedentsPersonnels = [];
    this.habits = [...this.habitsDefault];
    this.pendingCreations = [];
    this.pendingDeactivations = [];
    this.pendingHabits = [];
    this.gynecologicalHistory = {};
    this.pendingGyneco = false;
    this.familyHistory = {};
    this.familyMembers = [];
    this.pendingFamily = false;
  }

  private loadMedicalRecord(passportNumber: string): void {
    this.medicalRecordLoading = true;
    this.medicalRecordService.getMedicalRecord(passportNumber).subscribe({
      next: (data) => {
        this.comorbidities =
          data.comorbidities?.length > 0
            ? this.mapToComorbidities(data.comorbidities)
            : [];
        const precedentItems = data.precedents ?? data.antecedents ?? [];
        this.precedents =
          precedentItems.length > 0
            ? this.mapToComorbidities(precedentItems)
            : [];
        this.antecedentsPersonnels =
          data.antecedents && data.antecedents.length > 0
            ? this.mapToAntecedents(data.antecedents)
            : [];
        this.habits = this.buildHabitsForDisplay(data.habits ?? []);
        this.gynecologicalHistory = this.normalizeGynecoHistory(
          data.gynecologicalHistory || {},
        );
        this.pendingGyneco = false;
        this.familyHistory = data.familyHistory || {};
        this.familyMembers = this.familyHistory.familyMembers ?? [];
        this.familyHistory.familyMembers = this.familyMembers;
        this.pendingFamily = false;
        this.medicalRecordLoading = false;
      },
      error: () => {
        this.comorbidities = [];
        this.precedents = [];
        this.antecedentsPersonnels = [];
        this.medicalRecordLoading = false;
      },
    });
  }

  private mapToComorbidities(
    items: MedicalRecordItem[],
  ): (Omit<HealthCondition, '_id'> & { _id: string | null; code?: string })[] {
    return items.map((item) => ({
      _id: item._id ?? null,
      code: item.code ?? '',
      label: item.label ?? '',
      text: item.text ?? '',
      clinicalStatus: item.clinicalStatus ?? 'inactive',
      verificationStatus: '',
      category: '',
      version: 1,
      isCurrent: item.isCurrent ?? false,
      userId: '',
      fhirId: '',
      encounterId: '',
      createdAt: '',
      updatedAt: '',
    }));
  }

  private mapToAntecedents(items: MedicalRecordItem[]): (MedicalRecordItem & {
    code?: string;
    label?: string;
    isCurrent?: boolean;
  })[] {
    return items.map((item) => ({
      _id: item._id,
      code: item.code ?? '',
      label: item.label ?? '',
      text: item.text ?? '',
      clinicalStatus: item.clinicalStatus ?? 'inactive',
      isCurrent: item.isCurrent ?? false,
    }));
  }

  openComorbidityHistory(
    condition:
      | HealthCondition
      | (Omit<HealthCondition, '_id'> & { _id: string | null }),
  ): void {
    if (!condition._id) return;
    this.modalService.open({
      component: ComorbidityHistoryModalComponent,
      title: `Historique - ${condition.label}`,
      width: '480px',
      closable: true,
      data: {
        condition: condition as HealthCondition,
        patientName: `${this.currentUser?.firstName} ${this.currentUser?.lastName}`.trim() || 'Patient'
      },
    });
  }

  hasPendingCreation(code: string, type: 'comorbidity' | 'precedent'): boolean {
    return this.pendingCreations.some(
      (p) =>
        p.code === code && p.type === type && p.clinicalStatus === 'active',
    );
  }

  getSwitchState(
    item: { isCurrent?: boolean; clinicalStatus?: string },
    code: string,
    type: 'comorbidity' | 'precedent',
  ): boolean {
    const pendingActive = this.hasPendingCreation(code, type);
    const pendingDeact = this.pendingDeactivations.some(
      (p) => p.code === code && p.type === type,
    );
    const pendingInactive = this.pendingCreations.some(
      (p) =>
        p.code === code && p.type === type && p.clinicalStatus === 'inactive',
    );

    if (pendingActive) return true;
    if (pendingDeact || pendingInactive) return false;
    return !!(item.clinicalStatus === 'active');
  }


  /** Intercepte le clic : ouvre le modal si passage à true, désactive si passage à false */
  handleCreateSwitchClick(
    event: Event,
    item: {
      label?: string;
      code?: string;
      _id?: string | null;
      isCurrent?: boolean;
      clinicalStatus?: string;
    },
    type: 'comorbidity' | 'precedent',
  ): void {
    event.preventDefault();
    event.stopPropagation();
    const checked = (event.target as HTMLInputElement).checked;
    if (checked) {
      if (item.code && this.hasPendingCreation(item.code, type)) return;
      this.openAddCommentModal(item, type);
    } else {
      if (item._id && item.code) {
        item.isCurrent = false;
        item.clinicalStatus = 'inactive';
        this.pendingDeactivations.push({
          conditionId: item._id,
          type,
          code: item.code,
          label: item.label ?? '',
        });
      } else if (item.code) {
        this.removeFromPending(item.code, type);
      }
    }
  }

  onComorbidityChange(
    checked: boolean,
    item: Omit<HealthCondition, '_id'> & { _id: string | null; code?: string },
  ): void {
    if (
      item._id == null &&
      !checked &&
      item.code &&
      this.hasPendingCreation(item.code, 'comorbidity')
    ) {
      this.removeFromPending(item.code, 'comorbidity');
      return;
    }
    if (item._id == null && checked) return;
    if (item._id) {
      item.isCurrent = checked;
      item.clinicalStatus = checked ? 'active' : 'inactive';
      // TODO: appeler l'API de mise à jour si disponible (PATCH /health-conditions/:id)
    }
  }

  onAntecedentChange(
    checked: boolean,
    item: Omit<HealthCondition, '_id'> & { _id: string | null; code?: string },
  ): void {
    if (
      item._id == null &&
      !checked &&
      item.code &&
      this.hasPendingCreation(item.code, 'precedent')
    ) {
      this.removeFromPending(item.code, 'precedent');
      return;
    }
    if (item._id == null && checked) return;
    if (item._id) {
      item.isCurrent = checked;
      item.clinicalStatus = checked ? 'active' : 'inactive';
      // TODO: appeler l'API de mise à jour si disponible (PATCH /health-conditions/:id)
    }
  }

  private normalizeHabitLabel(code: string, fallbackLabel: string): string {
    if (code === 'fumeur') return 'Tabac';
    if (code === 'pratique-sport') return 'Pratique sport';
    if (code === 'alcool') return 'Ethylisme';
    if (code === 'habitudes-sexuelles') return 'Habitudes Sexuelles';
    return fallbackLabel;
  }

  private buildHabitsForDisplay(
    apiHabits: Array<{
      code?: string;
      label?: string;
      valueString?: string;
      note?: string;
      sevrage?: boolean;
      sevrageNote?: string;
    }>,
  ): {
    code?: string;
    label: string;
    valueString?: string;
    note?: string;
    sevrage?: boolean;
    sevrageNote?: string;
  }[] {
    const normalizedApi: Array<{
      code?: string;
      label: string;
      valueString?: string;
      note?: string;
      sevrage?: boolean;
      sevrageNote?: string;
    }> = apiHabits
      .map((h) => ({
        code: h.code ?? '',
        label: this.normalizeHabitLabel(h.code ?? '', h.label ?? ''),
        valueString: h.valueString ?? 'unknown',
        note: h.note,
        sevrage: h.sevrage,
        sevrageNote: h.sevrageNote,
      }))
      .filter((h) => h.label);

    const merged: Array<{
      code?: string;
      label: string;
      valueString?: string;
      note?: string;
      sevrage?: boolean;
      sevrageNote?: string;
    }> = [...normalizedApi];
    for (const defaultHabit of this.habitsDefault) {
      if (!merged.some((h) => h.code === defaultHabit.code)) {
        merged.push({ ...defaultHabit });
      }
    }

    return merged;
  }

  getHabitValue(item: { code?: string; valueString?: string }): string {
    const pending = this.pendingHabits.find((h) => h.code === item.code);
    if (pending) {
      return pending.valueString;
    }
    return item.valueString ?? 'unknown';
  }

  isHabitOui(item: { code?: string; valueString?: string }): boolean {
    return this.getHabitValue(item) === 'true';
  }

  isHabitNon(item: { code?: string; valueString?: string }): boolean {
    return this.getHabitValue(item) === 'false';
  }

  isHabitNonFait(item: { code?: string; valueString?: string }): boolean {
    const value = this.getHabitValue(item);
    return value !== 'true' && value !== 'false';
  }

  onHabitRadioChange(
    value: 'oui' | 'non' | 'non_fait',
    item: { code?: string; label: string; valueString?: string },
  ): void {
    if (!item.code) return;

    const valueString =
      value === 'oui' ? 'true' : value === 'non' ? 'false' : 'unknown';
    const existingIndex = this.pendingHabits.findIndex(
      (h) => h.code === item.code,
    );

    if (existingIndex > -1) {
      this.pendingHabits[existingIndex].valueString = valueString;
    } else {
      this.pendingHabits.push({
        code: item.code,
        label: item.label,
        valueString,
      });
    }
  }

  getHabitPrimaryComment(item: { code?: string; note?: string }): string {
    const pending = this.pendingHabits.find((h) => h.code === item.code);
    if (pending?.comment !== undefined) {
      return pending.comment;
    }
    const existing = this.habits.find((h) => h.code === item.code);
    return pending?.comment ?? existing?.note ?? item.note ?? '';
  }

  onHabitPrimaryCommentChange(
    comment: string,
    item: { code?: string; label: string; valueString?: string },
  ): void {
    if (!item.code) return;
    const idx = this.pendingHabits.findIndex((h) => h.code === item.code);
    if (idx > -1) {
      this.pendingHabits[idx].comment = comment;
      return;
    }

    this.pendingHabits.push({
      code: item.code,
      label: item.label,
      valueString: this.getHabitValue(item),
      comment,
    });
  }

  getHabitSevrageComment(item: { code?: string; sevrageNote?: string }): string {
    const pending = this.pendingHabits.find((h) => h.code === item.code);
    if (pending?.sevrageComment !== undefined) {
      return pending.sevrageComment;
    }
    const existing = this.habits.find((h) => h.code === item.code);
    return existing?.sevrageNote ?? item.sevrageNote ?? '';
  }

  onHabitSevrageCommentChange(
    comment: string,
    item: { code?: string; label: string; valueString?: string },
  ): void {
    if (!item.code) return;
    const idx = this.pendingHabits.findIndex((h) => h.code === item.code);
    if (idx > -1) {
      this.pendingHabits[idx].sevrageComment = comment;
      return;
    }

    this.pendingHabits.push({
      code: item.code,
      label: item.label,
      valueString: this.getHabitValue(item),
      sevrageComment: comment,
    });
  }

  getSportText(): string {
    return this.getHabitTextareaValue('pratique-sport');
  }

  onSportTextChange(text: string): void {
    this.upsertHabitTextValue('pratique-sport', 'Pratique sport', text);
  }

  private normalizeSportValue(value?: string): string {
    if (!value || value === 'true' || value === 'false' || value === 'unknown') {
      return '';
    }
    return value;
  }

  getSexualHabitsText(): string {
    return this.getHabitTextareaValue('habitudes-sexuelles');
  }

  onSexualHabitsTextChange(text: string): void {
    this.upsertHabitTextValue('habitudes-sexuelles', 'Habitudes Sexuelles', text);
  }

  private getHabitTextareaValue(code: string): string {
    const pending = this.pendingHabits.find((h) => h.code === code);
    if (pending) {
      return this.normalizeSportValue(pending.valueString);
    }
    const existing = this.habits.find((h) => h.code === code);
    return this.normalizeSportValue(existing?.valueString);
  }

  private upsertHabitTextValue(code: string, label: string, valueString: string): void {
    const idx = this.pendingHabits.findIndex((h) => h.code === code);
    if (idx > -1) {
      this.pendingHabits[idx].valueString = valueString;
      return;
    }

    this.pendingHabits.push({
      code,
      label,
      valueString,
    });
  }

  getHabitSevrageValue(item: { code?: string; sevrage?: boolean }): 'oui' | 'non' | 'non_fait' {
    const pending = this.pendingHabits.find((h) => h.code === item.code);
    if (pending?.sevrageValue === 'true') return 'oui';
    if (pending?.sevrageValue === 'false') return 'non';
    if (pending?.sevrageValue === 'unknown') return 'non_fait';
    const existing = this.habits.find((h) => h.code === item.code);
    if (existing?.sevrage === true || item.sevrage === true) return 'oui';
    if (existing?.sevrage === false || item.sevrage === false) return 'non';
    return 'non_fait';
  }

  onHabitSevrageChange(
    value: 'oui' | 'non' | 'non_fait',
    item: { code?: string; label: string; valueString?: string },
  ): void {
    if (!item.code) return;
    const sevrageValue =
      value === 'oui' ? 'true' : value === 'non' ? 'false' : 'unknown';
    const sevrage =
      sevrageValue === 'true'
        ? true
        : sevrageValue === 'false'
          ? false
          : undefined;
    const idx = this.pendingHabits.findIndex((h) => h.code === item.code);
    if (idx > -1) {
      this.pendingHabits[idx].sevrageValue = sevrageValue;
      this.pendingHabits[idx].sevrage = sevrage;
      return;
    }

    this.pendingHabits.push({
      code: item.code,
      label: item.label,
      valueString: this.getHabitValue(item),
      sevrageValue,
      sevrage,
    });
  }

  getGynecoRadioValue(field: 'contraception' | 'menopause'): 'oui' | 'non' | 'non_fait' {
    const val = this.gynecologicalHistory[field];
    if (val === 'oui' || val === true || val === 'Oui') return 'oui';
    if (val === 'non' || val === false || val === 'Non') return 'non';
    return 'non_fait';
  }

  onGynecoRadioChange(
    value: 'oui' | 'non' | 'non_fait',
    field: 'contraception' | 'menopause',
  ): void {
    this.gynecologicalHistory[field] = value;
    this.onGynecoChange();
  }

  private normalizeGynecoHistory(gyneco: GynecologicalHistory): GynecologicalHistory {
    return {
      ...gyneco,
      contraception: this.normalizeGynecoField(gyneco.contraception),
      menopause: this.normalizeGynecoField(gyneco.menopause),
    };
  }

  private normalizeGynecoField(value: unknown): 'oui' | 'non' | 'non_fait' {
    if (value === 'oui' || value === 'Oui' || value === true) return 'oui';
    if (value === 'non' || value === 'Non' || value === false) return 'non';
    return 'non_fait';
  }

  get gynecoIssueString(): string {
    return this.gynecologicalHistory.pregnancyOutcomes?.[0]?.issue ?? '';
  }

  set gynecoIssueString(val: string) {
    if (
      !this.gynecologicalHistory.pregnancyOutcomes ||
      this.gynecologicalHistory.pregnancyOutcomes.length === 0 ||
      !this.gynecologicalHistory.pregnancyOutcomes[0]
    ) {
      this.gynecologicalHistory.pregnancyOutcomes = [{}];
    }
    this.gynecologicalHistory.pregnancyOutcomes[0].issue = val;
  }

  onGynecoChange(): void {
    this.pendingGyneco = true;
  }

  onFamilyChange(): void {
    this.pendingFamily = true;
  }

  onFamilyMemberAdded(member: FamilyMember): void {
    if (!member?.firstName || !member?.lastName || !member?.relationship) return;
    this.familyMembers = [...this.familyMembers, member];
    this.familyHistory.familyMembers = this.familyMembers;
    this.onFamilyChange();
  }

  removeFamilyMember(index: number): void {
    this.familyMembers = this.familyMembers.filter((_, i) => i !== index);
    this.familyHistory.familyMembers = this.familyMembers;
    this.onFamilyChange();
  }

  openFamilyMemberModal(): void {
    this.modalService.open({
      component: FamilyMemberModalComponent,
      title: 'Ajouter un membre de famille',
      width: '420px',
      closable: true,
      data: { passportNumber: this.currentUser?.passportNumber },
    });
  }

  openHabitHistory(item: { code?: string; label?: string }): void {
    const passportNumber = this.currentUser?.passportNumber;
    if (!passportNumber || !item.code) return;

    this.medicalRecordService
      .getHabitHistory(passportNumber, item.code)
      .subscribe((versions) => {
        const rows = (versions ?? []).map((v: any) => ({
          date: this.formatVersionDate(v.createdAt),
          summary: this.formatHabitHistorySummary(v),
        }));

        this.modalService.open({
          component: MedicalRecordHistoryModalComponent,
          title: `Historique - ${item.label ?? item.code}`,
          width: '520px',
          closable: true,
          data: { title: `Historique - ${item.label ?? item.code}`, rows },
        });
      });
  }

  openGynecoHistory(): void {
    const passportNumber = this.currentUser?.passportNumber;
    if (!passportNumber) return;

    this.medicalRecordService
      .getGynecologicalHistoryVersions(passportNumber)
      .subscribe((versions) => {
        const rows = (versions ?? []).map((v: any) => ({
          date: this.formatVersionDate(v.createdAt),
          summary: this.formatGynecoHistorySummary(v),
        }));

        this.modalService.open({
          component: MedicalRecordHistoryModalComponent,
          title: 'Historique - Gynéco-obstétricaux',
          width: '520px',
          closable: true,
          data: { title: 'Historique - Gynéco-obstétricaux', rows },
        });
      });
  }

  openFamilyHistory(): void {
    const passportNumber = this.currentUser?.passportNumber;
    if (!passportNumber) return;

    this.medicalRecordService
      .getFamilyHistoryVersions(passportNumber)
      .subscribe((versions) => {
        const rows = (versions ?? []).map((v: any) => ({
          date: this.formatVersionDate(v.createdAt),
          summary: this.formatFamilyHistorySummary(v),
        }));

        this.modalService.open({
          component: MedicalRecordHistoryModalComponent,
          title: 'Historique - Familiaux',
          width: '520px',
          closable: true,
          data: { title: 'Historique - Familiaux', rows },
        });
      });
  }

  private formatVersionDate(value?: string): string {
    if (!value) return '-';
    const d = new Date(value);
    return Number.isNaN(d.getTime())
      ? '-'
      : d.toLocaleString('fr-FR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
  }

  private formatHabitHistorySummary(v: any): string {
    const value = v.valueString ?? 'non fait';
    const note = v.note ? ` | Commentaire: ${v.note}` : '';
    const sevrage =
      v.sevrage === true ? ' | Sevrage: oui' : v.sevrage === false ? ' | Sevrage: non' : '';
    const sevrageNote = v.sevrageNote ? ` | Commentaire sevrage: ${v.sevrageNote}` : '';
    return `Valeur: ${value}${sevrage}${note}${sevrageNote}`;
  }

  private formatGynecoHistorySummary(v: any): string {
    const contraception = v.contraception ?? 'non fait';
    const menopause = v.menopause ?? 'non fait';
    const contraceptionComment = v.contraceptionComment
      ? ` | Contraception: ${v.contraceptionComment}`
      : '';
    const menopauseComment = v.menopauseComment ? ` | Ménopause: ${v.menopauseComment}` : '';
    return `Contraception: ${contraception} | Ménopause: ${menopause}${contraceptionComment}${menopauseComment}`;
  }

  private formatFamilyHistorySummary(v: any): string {
    const parts = [
      v.ascendants ? `Ascendants: ${v.ascendants}` : '',
      v.fratrie ? `Fratrie: ${v.fratrie}` : '',
      v.pathologies ? `Pathologies: ${v.pathologies}` : '',
    ].filter(Boolean);

    const members = Array.isArray(v.familyMembers) ? v.familyMembers.length : 0;
    if (members > 0) {
      parts.push(`Membres: ${members}`);
    }
    return parts.length > 0 ? parts.join(' | ') : 'Aucune donnée';
  }

  private buildGynecologicalHistoryPayload(
    passportNumber: string,
  ): GynecologicalHistory & { passportNumber: string } {
    const pregnancyOutcomes = (
      this.gynecologicalHistory.pregnancyOutcomes ?? []
    )
      .map((outcome) => ({
        date: outcome.date,
        term: outcome.term?.trim() || undefined,
        mode: outcome.mode?.trim() || undefined,
        issue: outcome.issue?.trim() || undefined,
      }))
      .filter((outcome) =>
        Boolean(outcome.date || outcome.term || outcome.mode || outcome.issue),
      );

    return {
      ...this.gynecologicalHistory,
      pregnancyOutcomes:
        pregnancyOutcomes.length > 0 ? pregnancyOutcomes : undefined,
      passportNumber,
    };
  }

  isAntecedentOui(condition: any): boolean {
    const pending = this.pendingCreations.find(
      (p) => p.code === condition.code && p.type === 'precedent',
    );
    if (pending) return pending.clinicalStatus === 'active';
    const deact = this.pendingDeactivations.find(
      (p) => p.code === condition.code && p.type === 'precedent',
    );
    if (deact) return false;
    return condition._id != null && condition.clinicalStatus === 'active';
  }

  isAntecedentNon(condition: any): boolean {
    const pending = this.pendingCreations.find(
      (p) => p.code === condition.code && p.type === 'precedent',
    );
    if (pending) return pending.clinicalStatus === 'inactive';
    const deact = this.pendingDeactivations.find(
      (p) => p.code === condition.code && p.type === 'precedent',
    );
    if (deact) return true;
    return condition._id != null && condition.clinicalStatus === 'inactive';
  }

  isAntecedentNonFait(condition: any): boolean {
    return !this.isAntecedentOui(condition) && !this.isAntecedentNon(condition);
  }

  // ── Comorbidités ─────────────────────────────────────────────────────────

  isComorbidityOui(condition: any): boolean {
    const pending = this.pendingCreations.find(
      (p) => p.code === condition.code && p.type === 'comorbidity',
    );
    if (pending) return pending.clinicalStatus === 'active';
    const deact = this.pendingDeactivations.find(
      (p) => p.code === condition.code && p.type === 'comorbidity',
    );
    if (deact) return false;
    return condition._id != null && condition.clinicalStatus === 'active';
  }

  isComorbidityNon(condition: any): boolean {
    const pending = this.pendingCreations.find(
      (p) => p.code === condition.code && p.type === 'comorbidity',
    );
    if (pending) return pending.clinicalStatus === 'inactive';
    const deact = this.pendingDeactivations.find(
      (p) => p.code === condition.code && p.type === 'comorbidity',
    );
    if (deact) return true;
    return condition._id != null && condition.clinicalStatus === 'inactive';
  }

  isComorbidityNonFait(condition: any): boolean {
    return !this.isComorbidityOui(condition) && !this.isComorbidityNon(condition);
  }

  getConditionTextForInput(
    condition: Omit<HealthCondition, '_id'> & { _id: string | null; code?: string },
    type: 'comorbidity' | 'precedent',
  ): string {
    if (!condition.code) return '';

    const isOui =
      type === 'comorbidity'
        ? this.isComorbidityOui(condition)
        : this.isAntecedentOui(condition);
    if (!isOui) return '';

    const pendingActive = this.pendingCreations.find(
      (p) =>
        p.code === condition.code &&
        p.type === type &&
        p.clinicalStatus === 'active',
    );
    return pendingActive?.text ?? condition.text ?? '';
  }

  onConditionTextInput(
    text: string,
    condition: Omit<HealthCondition, '_id'> & { _id: string | null; code?: string },
    type: 'comorbidity' | 'precedent',
  ): void {
    if (!condition.code) return;

    const isOui =
      type === 'comorbidity'
        ? this.isComorbidityOui(condition)
        : this.isAntecedentOui(condition);
    if (!isOui) return;

    const pendingIndex = this.pendingCreations.findIndex(
      (p) =>
        p.code === condition.code &&
        p.type === type &&
        p.clinicalStatus === 'active',
    );

    if (pendingIndex > -1) {
      this.pendingCreations[pendingIndex].text = text;
      return;
    }

    this.pendingCreations.push({
      type,
      code: condition.code,
      label: condition.label ?? '',
      text,
      clinicalStatus: 'active',
    });
  }

  /** Gestion des radios Oui / Non / Non fait pour une comorbidité */
  onComorbidityRadioChange(
    value: 'oui' | 'non' | 'non_fait',
    condition: Omit<HealthCondition, '_id'> & {
      _id: string | null;
      code?: string;
    },
  ): void {
    if (!condition.code) return;

    this.removeFromPending(condition.code, 'comorbidity');

    if (value === 'oui') {
      // Le commentaire est saisi inline dans le champ de texte.
      this.pendingCreations.push({
        type: 'comorbidity',
        code: condition.code,
        label: condition.label ?? '',
        text: condition.text ?? '',
        clinicalStatus: 'active',
      });
    } else if (value === 'non') {
      // Créer une nouvelle version inactive
      this.pendingCreations.push({
        type: 'comorbidity',
        code: condition.code,
        label: condition.label ?? '',
        text: '',
        clinicalStatus: 'inactive',
      });
    } else if (value === 'non_fait') {
      if (condition._id) {
        // Condition existante : créer une nouvelle version 'unknown' (Non fait)
        this.pendingCreations.push({
          type: 'comorbidity',
          code: condition.code,
          label: condition.label ?? '',
          text: '',
          clinicalStatus: 'unknown',
        });
      }
      // Si pas de _id : déjà à l'état "Non fait", rien à faire
    }
  }

  /** Gestion des radios Oui / Non / Non fait pour un antécédent */
  onAntecedentRadioChange(
    value: 'oui' | 'non' | 'non_fait',
    condition: Omit<HealthCondition, '_id'> & {
      _id: string | null;
      code?: string;
    },
  ): void {
    if (!condition.code) return;

    this.removeFromPending(condition.code, 'precedent');

    if (value === 'oui') {
      // Le commentaire est saisi inline dans le champ de texte.
      this.pendingCreations.push({
        type: 'precedent',
        code: condition.code,
        label: condition.label ?? '',
        text: condition.text ?? '',
        clinicalStatus: 'active',
      });
    } else if (value === 'non') {
      // Créer une nouvelle version inactive
      this.pendingCreations.push({
        type: 'precedent',
        code: condition.code,
        label: condition.label ?? '',
        text: '',
        clinicalStatus: 'inactive',
      });
    } else if (value === 'non_fait') {
      if (condition._id) {
        // Condition existante : créer une nouvelle version 'unknown' (Non fait)
        this.pendingCreations.push({
          type: 'precedent',
          code: condition.code,
          label: condition.label ?? '',
          text: '',
          clinicalStatus: 'unknown',
        });
      }
      // Si pas de _id : déjà à l'état "Non fait", rien à faire
    }
  }

  private removeFromPending(
    code: string,
    type: 'comorbidity' | 'precedent',
  ): void {
    this.pendingCreations = this.pendingCreations.filter(
      (p) => !(p.code === code && p.type === type),
    );
    this.pendingDeactivations = this.pendingDeactivations.filter(
      (p) => !(p.code === code && p.type === type),
    );
  }

  private openAddCommentModal(
    item: { label?: string; code?: string },
    type: 'comorbidity' | 'precedent',
  ): void {
    const passportNumber = this.currentUser?.passportNumber;
    if (!passportNumber) return;

    this.modalService.open({
      component: HealthConditionCommentModalComponent,
      title: `Ajouter un commentaire - ${item.label ?? ''}`,
      width: '420px',
      closable: true,
      data: {
        label: item.label ?? '',
        type,
        code: item.code ?? '',
        passportNumber,
      },
    });
  }

  private onCreateHealthConditionFromModal(data: {
    comment: string;
    type: 'comorbidity' | 'precedent';
    code: string;
    label: string;
    passportNumber: string;
  }): void {
    if (!data?.passportNumber) return;

    // Si "Oui" a déjà ajouté une entrée active en attente (condition existante),
    // on met juste à jour le texte au lieu de créer une 2e entrée.
    const existingIndex = this.pendingCreations.findIndex(
      (p) =>
        p.code === data.code &&
        p.type === data.type &&
        p.clinicalStatus === 'active',
    );

    if (existingIndex > -1) {
      this.pendingCreations[existingIndex] = {
        ...this.pendingCreations[existingIndex],
        label: data.label,
        text: data.comment || '',
        clinicalStatus: 'active',
      };
      return;
    }

    this.pendingCreations.push({
      type: data.type,
      code: data.code,
      label: data.label,
      text: data.comment || '',
      clinicalStatus: 'active',
    });
  }

  savePendingChanges(): void {
    const passportNumber = this.currentUser?.passportNumber;
    if (!passportNumber) return;
    const hasCreations = this.pendingCreations.length > 0;
    const hasDeactivations = this.pendingDeactivations.length > 0;
    const hasHabits = this.pendingHabits.length > 0;
    const hasGyneco = this.pendingGyneco;
    const hasFamily = this.pendingFamily;

    if (
      !hasCreations &&
      !hasDeactivations &&
      !hasHabits &&
      !hasGyneco &&
      !hasFamily
    )
      return;

    this.savePendingLoading = true;

    const requests = [];

    if (hasCreations || hasDeactivations) {
      const conditions = [
        ...this.pendingCreations.map((p) => ({
          type: p.type,
          code: p.code,
          label: p.label,
          text: p.text || undefined,
          clinicalStatus: p.clinicalStatus,
        })),
        ...this.pendingDeactivations.map((p) => ({
          _id: p.conditionId,
          type: p.type,
          code: p.code,
          label: p.label,
          clinicalStatus: 'inactive' as const,
        })),
      ];

      requests.push(
        this.medicalRecordService.createHealthConditionsBatch(
          passportNumber,
          conditions,
        ),
      );
    }

    if (hasHabits) {
      const habitsData = this.pendingHabits.map((h) => ({
        code: h.code,
        label: h.label,
        category: h.code === 'pratique-sport' ? 'activity' : 'social-history',
        valueString: h.valueString,
        note: h.comment,
        sevrage: h.sevrage,
        sevrageNote: h.sevrageComment,
      }));
      requests.push(
        this.medicalRecordService.createHabitsBatch(passportNumber, habitsData),
      );
    }

    if (hasGyneco) {
      requests.push(
        this.medicalRecordService.createOrUpdateGynecologicalHistory({
          ...this.buildGynecologicalHistoryPayload(passportNumber),
        }),
      );
    }

    if (hasFamily) {
      requests.push(
        this.medicalRecordService.createOrUpdateFamilyHistory({
          ...this.familyHistory,
          passportNumber,
        }),
      );
    }

    forkJoin(requests)
      .pipe(
        finalize(() => {
          this.savePendingLoading = false;
        }),
      )
      .subscribe({
        next: () => this.onSaveSuccess(passportNumber),
        error: (err) => this.onSaveError(err),
      });
  }

  private onSaveSuccess(passportNumber: string): void {
    this.pendingCreations = [];
    this.pendingDeactivations = [];
    this.pendingHabits = [];
    this.pendingGyneco = false;
    this.pendingFamily = false;
    this.loadMedicalRecord(passportNumber);
    this.swalService.success('Succès', 'Données enregistrées avec succès.');
  }

  private onSaveError(err: unknown): void {
    console.error('Erreur sauvegarde conditions santé:', err);
    this.savePendingLoading = false;
  }
}
